
<template>
	<div class="diaries-page container">
		<nav-bar name="Mi Diario"></nav-bar>
		<template v-if="patient !== null">
			<div class="timeline px-3 py-4">
				<header class="timeline-header">
					<span
						class="tag is-txt-white is-bg-accent is-size-5 has-text-weight-bold"
						>Hoy</span
					>
				</header>

				<div
					class="timeline-item"
					v-for="date in sortedDates"
					:key="'diary' + date"
				>
					<div class="timeline-marker"></div>
					<div class="timeline-content has-text-left">
						<p class="heading">{{ formatDiaryDate(date) }}</p>
						<p>
							<span>
								<progress-indicator
									:max="patient.diaries[date].length"
									:step="patient.diaries[date].filter((qa) => qa.answer !== '').length"
                  message="Gana un dulce mientras completes tu diario todos los días"
								></progress-indicator>
							</span>
						</p>

						<span
							class="tag is-medium is-bg-primary is-txt-white is-size-6 has-text-weight-bold"
							@click="toggleQuestionForm"
							><i class="fa fa-address-book"></i>&nbsp;<span
								>Ver mi diario</span
							></span
						>
						<div class="question-form py-3">
							<div
								class="field"
								v-for="(qaItem, i) in patient.diaries[date]"
								:key="'question' + i"
							>
								<label class="label">{{ qaItem.questionLabel }}</label>
								<div class="control" v-if="editableTable[date]">
									<textarea
										rows="2"
										class="textarea"
										v-model="qaItem.answer"
										@input="onFinishTyping"
										placeholder="Textarea"
									>
									</textarea>
								</div>
								<div class="control" v-else>
									<div>{{ qaItem.answer }}</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>
		<template v-else>
			<div class="container">
				<div class="search-bar"><input type="text" /></div>
				<div class="results-container"></div>
			</div>
		</template>
	</div>
</template>

<script lang="ts">
	import NavBar from "@/components/NavBar.vue";
	import ProgressIndicator from "@/components/ProgressIndicator.vue";
	import { defineComponent, onMounted, reactive, ref } from "vue";
	import { Patient } from "@/domain/entities/Patient";
	import { DateTime } from "luxon";
	import useUser from "@/store/UserState";

	import { orderBy, debounce } from "lodash";
	import { pprint } from "@/helpers/pprint";
	import { onBeforeRouteLeave } from "vue-router";

	function formatDiaryDate(date: string) {
		return DateTime.fromFormat(date, 'dd/MM/y').toLocaleString({ weekday: 'long', month: 'long', day: '2-digit' })
	}
	function toggleQuestionForm(ev: Event) {
		((ev.currentTarget as HTMLElement)
			.nextElementSibling as HTMLElement).classList.toggle("active");
	}

	export default defineComponent({
		name: "Measurements",

		components: {
			ProgressIndicator,
			NavBar,
		},

		setup() {
			const {
				user: patient,
				fetchDiaryQuestions,
				updateDiary,
				MaxDiaryDays,
			} = useUser();
			const onFinishTyping = debounce((e) => {
				pprint(e);
				updateDiary();
			}, 1000);

			let editableTable: any = reactive({});
			let intervalCheckCurrentTime: any;
			let today = DateTime.local();

			const checkDiaryIsEditable = () => {
				for (let date in (patient.value as Patient).diaries!) {
					const diaryDate = DateTime.fromFormat(date, 'dd/MM/y');
					editableTable[date] = diaryDate.hasSame(today, 'day');
				}
			};
			let sortedDates = ref<string[]>([]);
			onMounted(async () => {
				await fetchDiaryQuestions();
				// date - bool
				sortedDates.value = orderBy(
					Object.keys((patient.value as Patient).diaries!),
					(o: any) => {
						const d = DateTime.fromFormat(o, 'dd/MM/y')
						return d.toMillis();
					},
					["desc"]
				).slice(0, MaxDiaryDays);
				checkDiaryIsEditable();
				intervalCheckCurrentTime = setInterval(() => {
					checkDiaryIsEditable();
				}, 5000);
			});
			onBeforeRouteLeave(() => {
				clearInterval(intervalCheckCurrentTime);
			});

			return {
				patient,
				formatDiaryDate,
				toggleQuestionForm,
				onFinishTyping,
				editableTable,
				sortedDates,
			};
		},
	});
</script>

<style lang="stylus" scoped>
	@import '../libs/timeline/timeline.min.css';

	.question-form {
		display: none;
	}

	.question-form.active {
		display: block;
	}
</style>
